import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatDistanceToNow,
  format,
  subDays,
  startOfDay,
  endOfDay,
  isWithinInterval,
  eachDayOfInterval,
  differenceInDays,
} from 'date-fns';
import { api, Article, UserStats, DailyViewStat, ArticleDailyView, CommentWithArticle } from '../utils/api';
import { useAuthStore } from '../stores/auth';
import { Pagination } from '../components/Pagination';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface DailyStats {
  date: string;
  articles: number;
}

interface DailyViewStats {
  date: string;
  views: number;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

type PresetRange = '7d' | '14d' | '30d' | '90d' | 'custom';

export function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [articles, setArticles] = useState<Article[]>([]);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dailyViewStats, setDailyViewStats] = useState<DailyViewStat[]>([]);
  const [articleDailyViews, setArticleDailyViews] = useState<ArticleDailyView[]>([]);
  const [comments, setComments] = useState<CommentWithArticle[]>([]);
  const [commentsPagination, setCommentsPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [filter, setFilter] = useState<'all' | 'published' | 'draft' | 'comments'>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  // Date range state - default to last 30 days
  const [presetRange, setPresetRange] = useState<PresetRange>('30d');
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: subDays(new Date(), 29),
    endDate: new Date(),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Update date range when preset changes
  const handlePresetChange = (preset: PresetRange) => {
    setPresetRange(preset);
    if (preset !== 'custom') {
      const days = preset === '7d' ? 7 : preset === '14d' ? 14 : preset === '30d' ? 30 : 90;
      setDateRange({
        startDate: subDays(new Date(), days - 1),
        endDate: new Date(),
      });
    }
    setShowDatePicker(false);
  };

  // Handle custom date changes
  const handleDateChange = (type: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    if (type === 'start') {
      setDateRange((prev) => ({ ...prev, startDate: newDate }));
    } else {
      setDateRange((prev) => ({ ...prev, endDate: newDate }));
    }
    setPresetRange('custom');
  };

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const status = filter === 'all' ? undefined : filter;
        const [articlesRes, allArticlesRes, statsRes] = await Promise.all([
          api.getMyArticles({ page: pagination.page, limit: 10, status }),
          api.getMyArticles({ page: 1, limit: 500 }), // Get more articles for chart data
          user ? api.getUserStats(user.id) : Promise.resolve({ stats: null }),
        ]);

        setArticles(articlesRes.articles);
        setAllArticles(allArticlesRes.articles);
        setPagination({
          page: articlesRes.pagination.page,
          totalPages: articlesRes.pagination.totalPages,
          total: articlesRes.pagination.total,
        });
        if (statsRes.stats) {
          setStats(statsRes.stats);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user, filter, pagination.page, navigate]);

  // Fetch daily view stats and top articles when date range changes
  useEffect(() => {
    if (!user) return;

    const fetchViewData = async () => {
      try {
        const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const endDate = format(dateRange.endDate, 'yyyy-MM-dd');

        const [dailyRes, byArticleRes] = await Promise.all([
          api.getDailyViewStats(user.id, startDate, endDate),
          api.getViewsByArticle(user.id, startDate, endDate),
        ]);

        setDailyViewStats(dailyRes.dailyViews);
        setArticleDailyViews(byArticleRes.dailyData);
      } catch (error) {
        console.error('Failed to fetch view stats:', error);
        setDailyViewStats([]);
        setArticleDailyViews([]);
      }
    };

    fetchViewData();
  }, [user, dateRange]);

  // Fetch comments when comments tab is selected
  useEffect(() => {
    if (filter !== 'comments') return;

    const fetchComments = async () => {
      setLoading(true);
      try {
        const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
        const endDate = format(dateRange.endDate, 'yyyy-MM-dd');
        const res = await api.getAllComments({
          page: commentsPagination.page,
          limit: 20,
          startDate,
          endDate,
        });
        setComments(res.comments);
        setCommentsPagination({
          page: res.pagination.page,
          totalPages: res.pagination.totalPages,
          total: res.pagination.total,
        });
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        setComments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [filter, dateRange, commentsPagination.page]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleCommentsPageChange = (page: number) => {
    setCommentsPagination((prev) => ({ ...prev, page }));
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      await api.deleteArticle(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
      if (stats) {
        setStats({
          ...stats,
          articles: stats.articles - 1,
        });
      }
    } catch (error) {
      console.error('Failed to delete article:', error);
      alert('Failed to delete article');
    }
  };

  const handleTogglePin = async (article: Article) => {
    const newPinState = article.is_pinned !== 1;
    try {
      const result = await api.pinArticle(article.id, newPinState);
      // Update article in both lists
      const updateFn = (prev: Article[]) =>
        prev.map((a) => (a.id === article.id ? { ...a, is_pinned: result.article.is_pinned, pin_order: result.article.pin_order } : a));
      setArticles(updateFn);
      setAllArticles(updateFn);
    } catch (error: any) {
      console.error('Failed to toggle pin:', error);
      alert(error.message || 'Failed to toggle pin');
    }
  };

  // Count pinned articles
  const pinnedCount = useMemo(() => {
    return allArticles.filter((a) => a.is_pinned === 1).length;
  }, [allArticles]);

  // Filter articles by date range
  const filteredArticles = useMemo(() => {
    return allArticles.filter((article) => {
      const articleDate = new Date(article.created_at);
      return isWithinInterval(articleDate, {
        start: startOfDay(dateRange.startDate),
        end: endOfDay(dateRange.endDate),
      });
    });
  }, [allArticles, dateRange]);

  // Calculate daily article counts for the selected date range
  const getDailyStats = useMemo((): DailyStats[] => {
    const days = eachDayOfInterval({
      start: dateRange.startDate,
      end: dateRange.endDate,
    });

    // Determine date format based on range
    const daysDiff = differenceInDays(dateRange.endDate, dateRange.startDate);
    const dateFormat = daysDiff > 30 ? 'MMM dd' : 'MMM dd';

    return days.map((day) => {
      const count = filteredArticles.filter((article) => {
        const articleDate = startOfDay(new Date(article.created_at));
        return articleDate.getTime() === startOfDay(day).getTime();
      }).length;

      return {
        date: format(day, dateFormat),
        articles: count,
      };
    });
  }, [dateRange, filteredArticles]);

  // Calculate daily view counts for the selected date range (from API data)
  const getDailyViewStats = useMemo((): DailyViewStats[] => {
    const days = eachDayOfInterval({
      start: dateRange.startDate,
      end: dateRange.endDate,
    });

    // Determine date format based on range
    const daysDiff = differenceInDays(dateRange.endDate, dateRange.startDate);
    const dateFormat = daysDiff > 30 ? 'MMM dd' : 'MMM dd';

    // Create a map of date -> views from API data
    const viewsMap = new Map<string, number>();
    dailyViewStats.forEach((stat) => {
      viewsMap.set(stat.date, stat.views);
    });

    // Fill in all dates with views (0 if no data)
    return days.map((day) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      return {
        date: format(day, dateFormat),
        views: viewsMap.get(dateKey) || 0,
      };
    });
  }, [dateRange, dailyViewStats]);

  // Calculate status distribution for pie chart (filtered by date range)
  const getStatusDistribution = useMemo(() => {
    const published = filteredArticles.filter((a) => a.status === 'published').length;
    const draft = filteredArticles.filter((a) => a.status === 'draft').length;
    return [
      { name: 'Published', value: published, color: '#10b981' },
      { name: 'Draft', value: draft, color: '#f59e0b' },
    ].filter((item) => item.value > 0);
  }, [filteredArticles]);

  // Calculate visibility distribution (filtered by date range)
  const getVisibilityDistribution = useMemo(() => {
    const publicCount = filteredArticles.filter((a) => a.visibility === 'public').length;
    const privateCount = filteredArticles.filter((a) => a.visibility === 'private').length;
    const unlistedCount = filteredArticles.filter((a) => a.visibility === 'unlisted').length;
    return [
      { name: 'Public', value: publicCount, color: '#3b82f6' },
      { name: 'Private', value: privateCount, color: '#ef4444' },
      { name: 'Unlisted', value: unlistedCount, color: '#8b5cf6' },
    ].filter((item) => item.value > 0);
  }, [filteredArticles]);

  // Calculate stats for the selected date range
  const rangeStats = useMemo(() => {
    const published = filteredArticles.filter((a) => a.status === 'published').length;
    const views = filteredArticles.reduce((sum, a) => sum + (a.view_count || 0), 0);
    return {
      articles: filteredArticles.length,
      published,
      views,
    };
  }, [filteredArticles]);

  // Prepare chart data showing daily top 5 articles with their breakdown
  const topArticlesChartData = useMemo(() => {
    if (articleDailyViews.length === 0) {
      return [];
    }

    const days = eachDayOfInterval({
      start: dateRange.startDate,
      end: dateRange.endDate,
    });

    const daysDiff = differenceInDays(dateRange.endDate, dateRange.startDate);
    const dateFormat = daysDiff > 30 ? 'MMM dd' : 'MMM dd';

    // Group views by date, keeping article info
    const viewsByDate = new Map<string, { article_id: string; title: string; views: number }[]>();
    articleDailyViews.forEach((item) => {
      if (!viewsByDate.has(item.date)) {
        viewsByDate.set(item.date, []);
      }
      viewsByDate.get(item.date)!.push({
        article_id: item.article_id,
        title: item.title,
        views: item.views,
      });
    });

    // Sort each day's articles by views and take top 5
    viewsByDate.forEach((articles, date) => {
      articles.sort((a, b) => b.views - a.views);
      viewsByDate.set(date, articles.slice(0, 5));
    });

    // Build chart data with top 5 articles breakdown for each day
    const chartData = days.map((day) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayArticles = viewsByDate.get(dateKey) || [];
      const totalViews = dayArticles.reduce((sum, a) => sum + a.views, 0);

      return {
        date: format(day, dateFormat),
        fullDate: dateKey,
        views: totalViews,
        // Store top 5 articles for tooltip
        topArticles: dayArticles.slice(0, 5),
      };
    });

    return chartData;
  }, [dateRange, articleDailyViews]);

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your content and analytics</p>
        </div>

        {/* Date Range Picker */}
        <div className="relative">
          <div className="flex items-center gap-2">
            {/* Preset buttons */}
            <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {(['7d', '14d', '30d', '90d'] as const).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetChange(preset)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    presetRange === preset
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  {preset === '7d' ? '7 Days' : preset === '14d' ? '14 Days' : preset === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>

            {/* Custom date picker toggle */}
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
                presetRange === 'custom'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">
                {format(dateRange.startDate, 'MMM d')} - {format(dateRange.endDate, 'MMM d, yyyy')}
              </span>
              <span className="sm:hidden">Custom</span>
            </button>
          </div>

          {/* Date picker dropdown */}
          {showDatePicker && (
            <div className="absolute right-0 mt-2 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
              <div className="flex flex-col gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={format(dateRange.startDate, 'yyyy-MM-dd')}
                    onChange={(e) => handleDateChange('start', e.target.value)}
                    max={format(dateRange.endDate, 'yyyy-MM-dd')}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={format(dateRange.endDate, 'yyyy-MM-dd')}
                    onChange={(e) => handleDateChange('end', e.target.value)}
                    min={format(dateRange.startDate, 'yyyy-MM-dd')}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards - Show range-specific stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-5 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Articles</p>
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-300 mt-1">{stats.articles}</p>
                <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                  {rangeStats.articles} in range
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-full">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="card p-5 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Published</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-300 mt-1">{stats.published}</p>
                <p className="text-xs text-green-500 dark:text-green-400 mt-1">
                  {rangeStats.published} in range
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-full">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="card p-5 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Total Views</p>
                <p className="text-3xl font-bold text-purple-700 dark:text-purple-300 mt-1">{stats.views}</p>
                <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                  {rangeStats.views} in range
                </p>
              </div>
              <div className="p-3 bg-purple-500/10 rounded-full">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="card p-5 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Comments</p>
                <p className="text-3xl font-bold text-orange-700 dark:text-orange-300 mt-1">{stats.comments}</p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-full">
                <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {!loading && allArticles.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Bar Chart - Articles per Day */}
          <div className="lg:col-span-2 card p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Articles Published ({format(dateRange.startDate, 'MMM d')} - {format(dateRange.endDate, 'MMM d, yyyy')})
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getDailyStats} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    className="text-gray-600 dark:text-gray-400"
                    stroke="currentColor"
                    interval={getDailyStats.length > 14 ? Math.floor(getDailyStats.length / 7) : 0}
                    angle={getDailyStats.length > 14 ? -45 : 0}
                    textAnchor={getDailyStats.length > 14 ? 'end' : 'middle'}
                    height={getDailyStats.length > 14 ? 60 : 30}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    className="text-gray-600 dark:text-gray-400"
                    stroke="currentColor"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, #fff)',
                      borderColor: 'var(--tooltip-border, #e5e7eb)',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'var(--tooltip-text, #111827)' }}
                  />
                  <Bar dataKey="articles" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Views Trend Chart */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 mt-6">
              Views Trend ({format(dateRange.startDate, 'MMM d')} - {format(dateRange.endDate, 'MMM d, yyyy')})
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getDailyViewStats} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    className="text-gray-600 dark:text-gray-400"
                    stroke="currentColor"
                    interval={getDailyViewStats.length > 14 ? Math.floor(getDailyViewStats.length / 7) : 0}
                    angle={getDailyViewStats.length > 14 ? -45 : 0}
                    textAnchor={getDailyViewStats.length > 14 ? 'end' : 'middle'}
                    height={getDailyViewStats.length > 14 ? 60 : 30}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    className="text-gray-600 dark:text-gray-400"
                    stroke="currentColor"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, #fff)',
                      borderColor: 'var(--tooltip-border, #e5e7eb)',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'var(--tooltip-text, #111827)' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    fill="url(#viewsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Daily Top Articles Chart */}
            {topArticlesChartData.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 mt-6">
                  Daily Top 5 Articles by Views
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topArticlesChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        className="text-gray-600 dark:text-gray-400"
                        stroke="currentColor"
                        interval={topArticlesChartData.length > 14 ? Math.floor(topArticlesChartData.length / 7) : 0}
                        angle={topArticlesChartData.length > 14 ? -45 : 0}
                        textAnchor={topArticlesChartData.length > 14 ? 'end' : 'middle'}
                        height={topArticlesChartData.length > 14 ? 60 : 30}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12 }}
                        className="text-gray-600 dark:text-gray-400"
                        stroke="currentColor"
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--tooltip-bg, #fff)',
                          borderColor: 'var(--tooltip-border, #e5e7eb)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                        }}
                        labelStyle={{ color: 'var(--tooltip-text, #111827)', fontWeight: 'bold', marginBottom: '4px' }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null;
                          const data = payload[0].payload as { topArticles: { title: string; views: number }[]; views: number };
                          return (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg max-w-xs">
                              <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total: {data.views} views</p>
                              {data.topArticles.length > 0 ? (
                                <ul className="space-y-1">
                                  {data.topArticles.map((article, idx) => (
                                    <li key={idx} className="text-xs text-gray-700 dark:text-gray-300 flex justify-between gap-2">
                                      <span className="truncate" style={{ maxWidth: '180px' }}>{article.title}</span>
                                      <span className="font-medium text-primary-600 dark:text-primary-400">{article.views}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-gray-500">No views</p>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="views" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* Pie Charts */}
          <div className="card p-5 space-y-6">
            {/* Status Distribution */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Status Distribution</h3>
              {getStatusDistribution.length > 0 ? (
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getStatusDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={25}
                        outerRadius={50}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {getStatusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend
                        verticalAlign="middle"
                        align="right"
                        layout="vertical"
                        iconSize={10}
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No data in selected range</p>
              )}
            </div>

            {/* Visibility Distribution */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Visibility</h3>
              {getVisibilityDistribution.length > 0 ? (
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getVisibilityDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={25}
                        outerRadius={50}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {getVisibilityDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend
                        verticalAlign="middle"
                        align="right"
                        layout="vertical"
                        iconSize={10}
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No data in selected range</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        {(['all', 'published', 'draft', 'comments'] as const).map((f) => (
          <button
            key={f}
            onClick={() => {
              setFilter(f);
              if (f === 'comments') {
                setCommentsPagination((prev) => ({ ...prev, page: 1 }));
              } else {
                setPagination((prev) => ({ ...prev, page: 1 }));
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              filter === f
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Content list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filter === 'comments' ? (
        // Comments list
        comments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">No comments found in selected date range</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="card p-4"
                >
                  <div className="flex items-start gap-3">
                    {comment.user_avatar ? (
                      <img
                        src={comment.user_avatar}
                        alt={comment.user_name || comment.author_name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                          {(comment.user_name || comment.author_name).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {comment.user_name || comment.author_name}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-2">{comment.content}</p>
                      {comment.article_title && (
                        <Link
                          to={`/article/${comment.article_slug}`}
                          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          on: {comment.article_title}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Pagination
                page={commentsPagination.page}
                totalPages={commentsPagination.totalPages}
                onPageChange={handleCommentsPageChange}
              />
            </div>
          </>
        )
      ) : articles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">No articles found</p>
          {user?.role === 'admin' && (
            <Link to="/write" className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
              Write your first article
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Pinned count indicator */}
          <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Pinned: {pinnedCount}/2
          </div>

          <div className="space-y-4">
            {articles.map((article) => (
              <div
                key={article.id}
                className={`card p-4 flex items-center justify-between gap-4 ${
                  article.is_pinned === 1 ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {article.is_pinned === 1 && (
                      <span className="badge text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" />
                          <path d="M10 12a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1z" />
                        </svg>
                        Pinned
                      </span>
                    )}
                    <Link
                      to={`/article/${article.slug}`}
                      className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 truncate"
                    >
                      {article.title}
                    </Link>
                    <span
                      className={`badge text-xs ${
                        article.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}
                    >
                      {article.status}
                    </span>
                    {article.visibility !== 'public' && (
                      <span
                        className={`badge text-xs ${
                          article.visibility === 'private'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {article.visibility}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>
                      {formatDistanceToNow(new Date(article.updated_at), {
                        addSuffix: true,
                      })}
                    </span>
                    <span>{article.view_count} views</span>
                    {article.parsedTags && article.parsedTags.length > 0 && (
                      <span className="text-gray-400 dark:text-gray-500">
                        {article.parsedTags.map((t) => `#${t}`).join(' ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTogglePin(article)}
                    disabled={article.is_pinned !== 1 && pinnedCount >= 2}
                    className={`btn-ghost text-sm px-3 py-1 ${
                      article.is_pinned === 1
                        ? 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                        : pinnedCount >= 2
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    title={
                      article.is_pinned === 1
                        ? 'Unpin article'
                        : pinnedCount >= 2
                        ? 'Maximum 2 articles can be pinned'
                        : 'Pin article to top'
                    }
                  >
                    {article.is_pinned === 1 ? 'Unpin' : 'Pin'}
                  </button>
                  <Link
                    to={`/edit/${article.id}`}
                    className="btn-ghost text-sm px-3 py-1 dark:text-gray-300"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(article.id)}
                    className="btn-ghost text-sm px-3 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
}
